// ============================================================
// 设计 Token —— 全站唯一视觉真源
// 清亮教育风 · 松绿(主)+ 冷中性灰双色体系
// 任何组件的颜色/圆角/阴影/间距都应引用这里,禁止散落硬编码。
//
// ⚠️ 角色约定(2026-08-11 换肤后):
//   accent = 松绿,是**品牌主色**(按钮/强调/选中/图表/关键数字)
//   brand  = 深墨蓝灰,是**墨色**(正文标题/深色小面积块/图标)
//   历史原因:换肤前 brand 是墨黑主色、accent 是点缀金,页面里
//   accent[700] 有 100+ 处引用。为了不动这些引用,换肤只换值不改名 ——
//   所以"主色"在 accent 里,别被名字误导。
// ============================================================

/**
 * 墨色阶:深墨蓝灰 —— 正文/标题/深色小色块/反白底图标。
 * 600 是最深的"墨",配松绿不打架(比纯黑柔和、比灰有骨头)。
 */
export const brand = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#334155', // 墨-中(次级标题/图标)
  600: '#1e293b', // 墨-深(主标题/关键数字)
  700: '#172033',
  800: '#0f172a',
  900: '#080d18',
};

/**
 * 主色阶:松绿 —— 品牌色。主按钮/选中态/强调/正向数据/图表主线。
 * 文字/图标用 700(#0f766e,白底对比度 4.8:1 达标),背景色块用 50–200 浅阶。
 */
export const accent = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  300: '#5eead4',
  400: '#2dd4bf',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e', // 主色松绿(文字/图标/主按钮)
  800: '#115e59',
  900: '#134e4a',
};

/**
 * 中性灰 —— 文字/边框/背景打底(冷中性,配松绿)。
 * ⚠️ 400 档原为 #94a3b8,白底对比度只有 2.56:1(远低于 WCAG AA 的 4.5)。
 * 它在全站被当"辅助文字/空态提示/底部导航未选中态"用,所以压深到 #64748b(4.76:1)。
 * 500 及更深各档同步下压一级,保证 400<500<600 的深浅次序不乱。
 * 纯装饰(分割线/占位图标)要浅色请用 300 或 200,别回头调 400。
 */
export const neutral = {
  0: '#ffffff',
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#64748b', // 辅助文字下限 —— 白底 4.76:1 达标
  500: '#546073', // 次要文字
  600: '#42506a',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

/** 语义色 */
export const semantic = {
  success: '#15803d',
  warning: '#b45309',
  error: '#b91c1c',
  info: '#0369a1',
};

/** 教师端成绩与工作台的浅墨蓝 + 香槟金辅助色。 */
export const brass = {
  50: '#fbf6ec',
  100: '#ead8b8',
  500: '#b58a4b',
  600: '#9b753f',
  700: '#8b6a39',
};

/** 文字色阶 */
export const text = {
  primary: neutral[800], // 主要文字
  secondary: neutral[500], // 次要文字
  tertiary: neutral[400], // 辅助/占位
  inverse: neutral[0], // 反白
};

/** 背景色 */
export const bg = {
  page: '#f8faf9', // 带一丝绿的暖白页面底
  card: neutral[0], // 卡片
  subtle: neutral[100], // 浅分区
};

/** 圆角 */
export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  pill: 999,
};

/** 阴影 —— 柔和、低对比(冷调投影,配松绿/冷灰) */
export const shadow = {
  sm: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.05)',
  md: '0 2px 8px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.04)',
  lg: '0 8px 24px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04)',
  brand: '0 6px 18px rgba(15, 118, 110, 0.24)', // 松绿主按钮浮起
  ink: '0 6px 18px rgba(30, 41, 59, 0.18)', // 墨色按钮浮起
};

/** 间距刻度(4 的倍数) */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
};

/** 字号 */
export const fontSize = {
  xs: 12,
  sm: 13,
  base: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
  display: 30,
};

/**
 * 品牌渐变。
 * ⚠️ hero / heroGold 换肤后是**浅色**底(原先是墨黑渐变)。
 * 页面里 Hero 区的文字必须用深色 —— 见下面的 hero.* 语义 token。
 * 名字保留是为了不改 20+ 个页面的引用。
 */
export const gradient = {
  // 松绿渐变(凸起主按钮/通栏入口)。起点用 700 不用 600：
  // 600(#0d9488) 上压白字只有 3.74:1,不够 4.5;700 起步后白字达标。
  brand: `linear-gradient(135deg, ${accent[700]} 0%, ${accent[900]} 100%)`,
  brandSoft: `linear-gradient(135deg, ${accent[50]} 0%, ${accent[100]} 100%)`,
  hero: `linear-gradient(170deg, #f4fbf9 0%, #eaf6f4 55%, #e2f2ef 100%)`, // 浅松绿雾面
  heroGold: `linear-gradient(170deg, #f6fbfa 0%, #edf5f9 60%, #e6f0f7 100%)`, // 浅青蓝雾面(区分二级模块)
  teacherHero: 'linear-gradient(135deg, #eaf0f4 0%, #f8fafc 72%)',
  teacherInk: 'linear-gradient(135deg, #203040 0%, #304a60 100%)',
};

/**
 * Hero 头区语义 token —— 浅底 Hero 的配套文字/控件色。
 * 换肤前 Hero 是墨黑底白字,现在是浅底深字,页面统一引这里,别再写 '#fff'。
 */
export const hero = {
  title: brand[600], // 大标题
  sub: neutral[500], // 副文案
  chipBg: 'rgba(255, 255, 255, 0.72)', // Hero 内的圆角控件底(半透白)
  chipBorder: accent[200], // 控件描边
  chipIcon: accent[700], // 控件内图标
  divider: 'rgba(15, 118, 110, 0.12)', // Hero 底边分隔
};

/**
 * 能力维度调色板 —— 8 个一级能力/雷达图各维度用色。
 * 松绿系打头统一气质,中段拉开冷暖,末尾点墨色收口,同屏可辨又不花。
 */
export const dimensionPalette = [
  accent[700], // 松绿
  '#0369a1', // 靛蓝
  '#0891b2', // 青
  '#4f46e5', // 蓝紫
  '#b45309', // 琥珀
  '#be185d', // 玫红
  '#65a30d', // 橄榄绿
  brand[500], // 墨
];

const tokens = {
  brand,
  accent,
  neutral,
  semantic,
  brass,
  text,
  bg,
  radius,
  shadow,
  space,
  fontSize,
  gradient,
  hero,
  dimensionPalette,
};

export default tokens;
